/**
 * AssociationChangedItem - Renders a stage/association change.
 *
 * Displays: "**Actor** changed **Stage**" with sub-item showing "Old → New"
 *
 * Used when exactly 1 association is removed and 1 is added in the same
 * timestamp by the same actor (e.g., moving a deal between pipeline stages).
 */

import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { EntityBadge } from '@/lib/crm/components/badges/EntityBadge';
import { useEntityTypeLabel } from '@/lib/crm/hooks/useEntityTypeLabel';
import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';
import { getEntityTypeSentenceLabel } from '@/lib/crm/utils/entityTypeUtils';
import { getAssociationTargetIconComponent } from '@/lib/crm/utils/icons/EntityTypeIcon';

import {
  ActorName,
  bold,
  InlineText,
  TimelineEvent,
} from '../components/shared';
import { useViewStateLookups } from '../context/TimelineLookupsContext';
import { isAssociationTypeVisible } from '../timelineItemVisibility';
import type { AssociationPayload } from '../types';
import { EVENT_TYPES } from '../types';
import type { SubItemContent } from '../types/renderer';
import { useEventFormatters } from '../useEventFormatters';
import type { ItemComponentProps } from './types';

export function AssociationChangedItem({
  item,
  entityType,
}: ItemComponentProps) {
  const { orgId } = useParams<{ orgId: string }>();
  const { getActorInfo, getObjectLabel } = useEventFormatters();
  const { lookups } = useViewStateLookups();
  const { getEntityTypeLabel } = useEntityTypeLabel(
    orgId ? Number(orgId) : undefined
  );
  const openSlideout = useOpenSlideoutOptional();

  const handleEntityClick = useCallback(
    (entityId: string) => {
      openSlideout?.(entityId);
    },
    [openSlideout]
  );

  // Extract removed and added events (guaranteed 1 each by transform)
  const removedEvent = item.events.find(
    e => e.event_type_name === EVENT_TYPES.ASSOCIATION_REMOVED
  );
  const addedEvent = item.events.find(
    e => e.event_type_name === EVENT_TYPES.ASSOCIATION_ADDED
  );

  // Pre-compute payloads and derived objects before early returns so all
  // useCallback hooks are called unconditionally.
  const removedPayload = removedEvent?.payload as
    | AssociationPayload
    | undefined;
  const addedPayload = addedEvent?.payload as AssociationPayload | undefined;

  const assocTypeName = addedPayload?.association_type_name ?? '';
  const associationType = lookups.association_types[assocTypeName];

  const isAssociatedEvent = addedEvent
    ? 'association_type_name' in addedEvent
    : false;
  const associatedObject =
    isAssociatedEvent && addedEvent
      ? lookups.entities[addedEvent.entity_id]
      : undefined;

  const oldTargetObject = removedPayload?.target_entity_id
    ? lookups.entities[removedPayload.target_entity_id]
    : undefined;
  const newTargetObject = addedPayload?.target_entity_id
    ? lookups.entities[addedPayload.target_entity_id]
    : undefined;

  const handleAssociatedObjectClick = useCallback(() => {
    if (associatedObject) {
      handleEntityClick(associatedObject.entity_id);
    }
  }, [handleEntityClick, associatedObject]);
  const handleOldTargetClick = useCallback(() => {
    if (oldTargetObject) {
      handleEntityClick(oldTargetObject.entity_id);
    }
  }, [handleEntityClick, oldTargetObject]);
  const handleNewTargetClick = useCallback(() => {
    if (newTargetObject) {
      handleEntityClick(newTargetObject.entity_id);
    }
  }, [handleEntityClick, newTargetObject]);

  if (!removedEvent || !addedEvent) {
    return null;
  }

  const actor = getActorInfo(addedEvent);

  // Hide if association type not in lookups or is internal (fail-closed)
  if (!isAssociationTypeVisible(associationType)) {
    return null;
  }

  // For associated events, get the object label from lookups
  // For primary events, use "this {entityType}"
  const entityTypeDisplayLabel = getEntityTypeSentenceLabel(
    entityType,
    getEntityTypeLabel(entityType)
  );
  const objectDisplayText = isAssociatedEvent
    ? getObjectLabel(addedEvent.entity_id)
    : `this ${entityTypeDisplayLabel}`;

  // Render associated object as badge if available, otherwise use text
  const isNoteAssocObject = associatedObject?.entity_type_name === 'ahoy_note';
  let objectDisplay: ReactNode;
  if (isAssociatedEvent && associatedObject && !isNoteAssocObject) {
    objectDisplay = (
      <EntityBadge
        state={{
          kind: 'data',
          data: {
            id: associatedObject.entity_id,
            entityType: associatedObject.entity_type_name,
            displayName: associatedObject.ahoy_display_name ?? 'Unknown',
          },
        }}
        variant='xs'
        className='inline-flex align-middle'
        onClick={openSlideout ? handleAssociatedObjectClick : undefined}
      />
    );
  } else if (isAssociatedEvent) {
    objectDisplay = bold(objectDisplayText);
  } else {
    objectDisplay = objectDisplayText;
  }

  const relationshipLabel = associationType?.label || 'association';

  const oldTargetLabel =
    removedPayload?.target_label ||
    (removedPayload?.target_entity_id
      ? getObjectLabel(removedPayload.target_entity_id)
      : null) ||
    'record';

  const newTargetLabel =
    addedPayload?.target_label ||
    (addedPayload?.target_entity_id
      ? getObjectLabel(addedPayload.target_entity_id)
      : null) ||
    'record';

  // Render targets as EntityBadge for rich objects, text fallback otherwise
  const isOldNoteObject = oldTargetObject?.entity_type_name === 'ahoy_note';
  const oldTargetDisplay =
    oldTargetObject && !isOldNoteObject ? (
      <EntityBadge
        state={{
          kind: 'data',
          data: {
            id: oldTargetObject.entity_id,
            entityType: oldTargetObject.entity_type_name,
            displayName: oldTargetObject.ahoy_display_name ?? 'Unknown',
          },
        }}
        variant='xs'
        className='inline-flex align-middle'
        onClick={openSlideout ? handleOldTargetClick : undefined}
      />
    ) : (
      <span className='font-medium text-tertiary'>{oldTargetLabel}</span>
    );

  const isNewNoteObject = newTargetObject?.entity_type_name === 'ahoy_note';
  const newTargetDisplay =
    newTargetObject && !isNewNoteObject ? (
      <EntityBadge
        state={{
          kind: 'data',
          data: {
            id: newTargetObject.entity_id,
            entityType: newTargetObject.entity_type_name,
            displayName: newTargetObject.ahoy_display_name ?? 'Unknown',
          },
        }}
        variant='xs'
        className='inline-flex align-middle'
        onClick={openSlideout ? handleNewTargetClick : undefined}
      />
    ) : (
      <span className='font-medium'>{newTargetLabel}</span>
    );

  const ariaLabel = `${actor.name} changed ${relationshipLabel} on ${objectDisplayText} from ${oldTargetLabel} to ${newTargetLabel}`;

  // Get icon based on target entity type from association schema
  const IconComponent = getAssociationTargetIconComponent(
    associationType ?? {}
  );

  // Build sub-item showing old → new
  const subItems: SubItemContent[] = [
    {
      icon: <IconComponent className='w-4 h-4 text-primary' />,
      text: (
        <span className='inline-flex flex-wrap items-center gap-1.5 max-w-full'>
          {oldTargetDisplay}
          <span className='text-tertiary'>→</span>
          {newTargetDisplay}
        </span>
      ),
      ariaLabel: `${oldTargetLabel} to ${newTargetLabel}`,
    },
  ];

  return (
    <TimelineEvent ariaLabel={ariaLabel}>
      <TimelineEvent.Icon event_type_name={EVENT_TYPES.ASSOCIATION_ADDED} />
      <TimelineEvent.Content
        header={
          <InlineText>
            <ActorName actor={actor} /> changed {bold(relationshipLabel)} on{' '}
            {objectDisplay}
          </InlineText>
        }
        timestamp={addedEvent.occurred_at}
        subItems={subItems}
      />
    </TimelineEvent>
  );
}
